import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children, apiUrl, user, exerciseId }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [fieldLocks, setFieldLocks] = useState({});
  const [phaseChangeEvent, setPhaseChangeEvent] = useState(null); // { phase, changed_by_name, changed_by_position, changed_at, ts }
  const currentFormRef = useRef('');

  // Connect socket when user and exercise are available
  useEffect(() => {
    if (!user?.id || !exerciseId || !apiUrl) return;

    const wsUrl = apiUrl.replace(/\/+$/, '');
    const socket = io(wsUrl, {
      path: '/api/ws/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_exercise', {
        exercise_id: exerciseId,
        user_id: user.id,
        user_name: user.name || user.email || 'Unknown',
        form_id: currentFormRef.current,
      });
    });

    socket.on('connect_error', (err) => {
      console.warn('[WS] connect_error:', err.message);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('presence_update', (data) => {
      setOnlineUsers(data.users || []);
    });

    socket.on('field_locked', (data) => {
      setFieldLocks(prev => ({
        ...prev,
        [`${data.form_id}:${data.field_key}`]: {
          user_id: data.user_id,
          user_name: data.user_name,
        },
      }));
    });

    socket.on('field_unlocked', (data) => {
      setFieldLocks(prev => {
        const next = { ...prev };
        delete next[`${data.form_id}:${data.field_key}`];
        return next;
      });
    });

    socket.on('current_locks', (data) => {
      const newLocks = {};
      for (const [fieldKey, lockInfo] of Object.entries(data.locks || {})) {
        if (lockInfo.user_id !== user.id) {
          newLocks[`${data.form_id}:${fieldKey}`] = lockInfo;
        }
      }
      setFieldLocks(prev => ({ ...prev, ...newLocks }));
    });

    socket.on('lock_denied', (data) => {
      console.warn(`[WS] Lock denied on ${data.field_key}: locked by ${data.locked_by}`);
    });

    socket.on('emergency_phase_changed', (data) => {
      // Broadcast event from backend when the IC (or authorized user) changes the phase
      setPhaseChangeEvent({
        ...data,
        ts: Date.now(), // unique timestamp so consumers can detect repeat events
      });
    });

    return () => {
      socket.emit('leave_exercise', { exercise_id: exerciseId, user_id: user.id });
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setOnlineUsers([]);
      setFieldLocks({});
    };
  }, [user?.id, exerciseId, apiUrl]);

  const updateForm = useCallback((formId) => {
    currentFormRef.current = formId;
    if (socketRef.current?.connected) {
      socketRef.current.emit('update_form', {
        exercise_id: exerciseId,
        form_id: formId,
      });
    }
  }, [exerciseId]);

  const lockField = useCallback((formId, fieldKey) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('lock_field', {
        exercise_id: exerciseId,
        form_id: formId,
        field_key: fieldKey,
        user_id: user?.id,
        user_name: user?.name || user?.email || 'Unknown',
      });
    }
  }, [exerciseId, user]);

  const unlockField = useCallback((formId, fieldKey) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unlock_field', {
        exercise_id: exerciseId,
        form_id: formId,
        field_key: fieldKey,
        user_id: user?.id,
      });
    }
  }, [exerciseId, user]);

  const isFieldLocked = useCallback((formId, fieldKey) => {
    const lock = fieldLocks[`${formId}:${fieldKey}`];
    if (!lock) return null;
    if (lock.user_id === user?.id) return null;
    return lock;
  }, [fieldLocks, user?.id]);

  const value = {
    socket: socketRef.current,
    connected,
    onlineUsers,
    fieldLocks,
    phaseChangeEvent,
    clearPhaseChangeEvent: () => setPhaseChangeEvent(null),
    updateForm,
    lockField,
    unlockField,
    isFieldLocked,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
